import { WEALTH_MULTIPLIER, type ItemId, type RecruitKind, type Wealth, type WeaponId } from '@cratered/shared';

/**
 * The buy phase. Every round starts with 20 s of shopping: each team gets income, spends it on
 * grenades, mines, rifles, rockets and gear, and the round begins when everyone is ready. Every
 * bought thing starts at one per team (see weapons.ts), so the first round is a taster.
 *
 * Calibration rule (see scripts/balance.ts and client/test/economy.test.ts): the starting
 * budget must buy 1.5× the average number of an attack it takes to win when a team uses only
 * that attack in bot-vs-bot matches. Prices are derived from that measurement, so changing the
 * sim or the bots means re-running `npm run balance`. Gear cannot win a fight on its own, so it
 * is priced by hand.
 */

export type PurchasableId = Exclude<WeaponId, 'ball' | 'gun' | 'boot' | 'catapult'>;
/** Attacks priced by the calibration rule. */
export type CalibratedId = 'grenade' | 'bignade' | 'cluster' | 'mine' | 'sniper' | 'mortar' | 'rpg' | 'sticky' | 'rubber' | 'airstrike' | 'railgun';
export type GearId = 'grapple' | 'platform' | 'bridge' | 'teleport' | 'sandbags' | 'medkit' | 'repair' | 'pickaxe' | 'tunneler';
export const CALIBRATED: readonly CalibratedId[] = ['grenade', 'bignade', 'cluster', 'mine', 'sniper', 'mortar', 'rpg', 'sticky', 'rubber', 'airstrike', 'railgun'];
export const GEAR: readonly GearId[] = ['grapple', 'platform', 'bridge', 'teleport', 'sandbags', 'medkit', 'repair', 'pickaxe', 'tunneler'];
/**
 * Priced by hand: gear, the harpoon, the shotgun and the melee weapons (bat, mallet, pike: two
 * crews that can only swing never meet across water). Bot-only matches with just one of those
 * mostly never end (2026-11-02: harpoon 29 of 32 drew; a one-seed shotgun sample drew on all
 * eight levels): the harpoon barely wounds and bots do not fish for the water, and two shotgun
 * crews cannot cross the water to get in range of each other. The rule would price the harpoon
 * at 40 and the shotgun on a handful of pit-map wins, so both take the price the measured hints
 * suggested and stay there by hand. The SMG is a pistol with more rounds (same line-of-sight
 * problem for bots), the crossbow has no blast for a bot's lob search to land near, and the TNT
 * charge only reaches arm's length, so those three are hand-priced too. The massive bomb costs a flat 400: the user's call, and it keeps the huge crater a deliberate purchase.
 */
export const HAND_PRICED: readonly PurchasableId[] = ['shotgun', 'harpoon', 'bat', 'mallet', 'pike', 'smg', 'crossbow', 'tnt', 'massive', ...GEAR];
export const PURCHASABLE: readonly PurchasableId[] = [...CALIBRATED, ...HAND_PRICED];

export const STARTING_COINS = 1000;
/** Versus money modes scale every coin that comes in (starting purse, round income, bounties). */
export function coinsFor(amount: number, wealth: Wealth | undefined): number {
  return Math.round(amount * WEALTH_MULTIPLIER[wealth ?? 'normal']);
}
/**
 * Versus is best of three. Every round opens with a buy phase: unspent coins carry over, both
 * teams get ROUND_INCOME, the round winner gets a little more, and cool hits paid out as they
 * happened. Co-op shops before every wave; the campaign shops once.
 */
export const ROUND_INCOME = 350;
export const BOUNTIES = {
  kill: 150,
  /** On top of a kill, for knocking someone into the water or off the map. */
  splash: 50,
  headshot: 40,
  longShot: 30,
  directHit: 20,
  roundWin: 250,
  roundLoss: 100,
} as const;
export const BUY_SECONDS = 20;
export const BUY_TICKS = BUY_SECONDS * 60;
/** Most of one item a single buy input may add or remove. */
export const MAX_BUY_COUNT = 20;

/**
 * Measured by `npm run balance` (bot vs bot at difficulty 0.9, one attack only, one fight,
 * 8 levels × 4 seeds, draws excluded): average uses by the winning team.
 * Prices = floor(STARTING_COINS / (1.5 × avg)) rounded down to 5.
 */
export const CALIBRATION = {
  budgetMultiplier: 1.5,
  /** 2026-11-03, over the 9 Raft Wars arenas and 20 war maps, 4 seeds each, after the third arsenal expansion. */
  averageUsesToWin: {
    grenade: 10.6, bignade: 5.2, cluster: 7.9, mine: 7.1, sniper: 4.2, mortar: 4.5, rpg: 4.2,
    sticky: 4.4, rubber: 7.3, airstrike: 2.4, railgun: 2.4,
  } as Record<CalibratedId, number>,
};

export const PRICES: Record<PurchasableId, number> = {
  grenade: 60,
  bignade: 125,
  cluster: 80,
  mine: 90,
  shotgun: 70,
  sniper: 155,
  harpoon: 110,
  bat: 80,
  mallet: 90,
  pike: 60,
  mortar: 145,
  rpg: 155,
  smg: 85,
  sticky: 150,
  rubber: 90,
  airstrike: 275,
  massive: 400,
  crossbow: 90,
  railgun: 275,
  tnt: 120,
  // Gear, by hand: cheap enough to try every round, dear enough that a crew cannot wall itself in every turn.
  grapple: 60,
  platform: 80,
  bridge: 120,
  teleport: 90,
  sandbags: 50,
  medkit: 120,
  repair: 100,
  pickaxe: 70,
  tunneler: 110,
};

/**
 * Allies. A floatie mole sits on an inflatable (or, over a pit, a balloon crate) beside your side
 * of the map: it cannot move and one solid hit knocks it off into the drink, so it is cheap. A boat
 * mate stands on the raft with the standard moles and plays like one, so it costs more.
 */
export const RECRUIT_PRICES: Record<RecruitKind, number> = { floatie: 250, boat: 500 };
export const ALLY_HP: Record<RecruitKind, number> = { floatie: 70, boat: 80 };
/** Standard moles plus allies, per team. */
export const MAX_TEAM_SIZE = 6;

/**
 * Kit: bought once, kept for the whole match (it survives the field reset between fights).
 * The last-shot ghost draws the path your previous shot took while you aim the next one, the way
 * Raft Wars and Worms show the last trajectory, so a near miss can be walked onto the target.
 */
export const ITEMS: Record<ItemId, { name: string; price: number; blurb: string }> = {
  ghost: { name: 'Last-shot ghost', price: 90, blurb: 'Shows the path of your previous shot while you aim. Lasts the match.' },
};
export const ITEM_IDS: readonly ItemId[] = ['ghost'];
/** A floatie mole stays put unless a hit shoves it at least this hard (a pistol tap does not). */
export const FLOATIE_KNOCKOFF = 4;

export function isPurchasable(w: WeaponId): w is PurchasableId {
  return (PURCHASABLE as readonly string[]).includes(w);
}

export function isCalibrated(w: WeaponId): w is CalibratedId {
  return (CALIBRATED as readonly string[]).includes(w);
}

export function isGearId(w: WeaponId): w is GearId {
  return (GEAR as readonly string[]).includes(w);
}

export function priceOf(w: WeaponId): number {
  return isPurchasable(w) ? PRICES[w] : 0;
}

/** Highest price that still satisfies the calibration rule for a measured average. */
export function calibratedPrice(averageUses: number, budget = STARTING_COINS, multiplier = CALIBRATION.budgetMultiplier): number {
  if (averageUses <= 0) return budget;
  return Math.max(5, Math.floor(budget / (multiplier * averageUses) / 5) * 5);
}
